import { useAuthStore } from '@sentinel/web-core';
import { createRoute, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router';
import { SPACES } from '@/shell/nav/navigation';
import { landingPath, visibleSpaces } from '@/shell/nav/spaces';
import { appRoute } from './root-routes';

/** Routes that need an organization context: platform users without a grant go to their home. */
export const organizationRoute = createRoute({
  getParentRoute: () => appRoute,
  id: 'organization',
  component: Outlet,
  beforeLoad: () => {
    const me = useAuthStore.getState().me;
    if (me !== null && me.organization === null)
      throw redirect({ to: me.context === 'console' ? '/platform' : '/denied' });
  },
});

const parent = () => organizationRoute;

/** Operational screens of the organization, one route each (paths stay literal for the types).
 *  Every page is its own chunk: the first paint carries the shell only, and the router preloads
 *  a page when the pointer rests on its link (`defaultPreload: 'intent'`). */
export const organizationPages = [
  createRoute({
    getParentRoute: parent,
    path: '/',
    beforeLoad: () => {
      const me = useAuthStore.getState().me;
      const landing = me === null ? null : landingPath(visibleSpaces(SPACES, me.permissions));
      if (landing !== null) throw redirect({ to: landing });
    },
    component: lazyRouteComponent(
      () => import('@/modules/dashboard/DashboardPage'),
      'DashboardPage',
    ),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/operations',
    component: lazyRouteComponent(
      () => import('@/modules/operations/OperationsPage'),
      'OperationsPage',
    ),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/teams',
    component: lazyRouteComponent(() => import('@/modules/teams/TeamsPage'), 'TeamsPage'),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/personnel',
    component: lazyRouteComponent(() => import('@/modules/inventory/PersonsPage'), 'PersonsPage'),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/personnel/registry',
    component: lazyRouteComponent(() => import('@/modules/personnel/RegistryPage'), 'RegistryPage'),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/resources',
    component: lazyRouteComponent(
      () => import('@/modules/inventory/ResourcesPage'),
      'ResourcesPage',
    ),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/devices',
    component: lazyRouteComponent(() => import('@/modules/inventory/DevicesPage'), 'DevicesPage'),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/timeline',
    component: lazyRouteComponent(() => import('@/modules/inventory/TimelinePage'), 'TimelinePage'),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/drones',
    component: lazyRouteComponent(() => import('@/modules/drones/DronesPage'), 'DronesPage'),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/drones/registry',
    component: lazyRouteComponent(
      () => import('@/modules/drones/registry/DroneRegistryPage'),
      'DroneRegistryPage',
    ),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/map',
    component: lazyRouteComponent(() => import('@/modules/map/page/MapPage'), 'MapPage'),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/communications',
    component: lazyRouteComponent(
      () => import('@/modules/communications/CommunicationsPage'),
      'CommunicationsPage',
    ),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/replay',
    component: lazyRouteComponent(
      () => import('@/modules/tracking/replay/ReplayPage'),
      'ReplayPage',
    ),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/tracks',
    component: lazyRouteComponent(
      () => import('@/modules/tracking/tracks/TracksPage'),
      'TracksPage',
    ),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/reports',
    component: lazyRouteComponent(() => import('@/modules/reports/ReportsPage'), 'ReportsPage'),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/reports/$missionId',
    component: lazyRouteComponent(() => import('@/modules/reports/ReportPage'), 'ReportPage'),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/field',
    component: lazyRouteComponent(() => import('@/modules/field/FieldPage'), 'FieldPage'),
  }),
  createRoute({
    getParentRoute: parent,
    path: '/connectivity',
    component: lazyRouteComponent(
      () => import('@/modules/connectivity/ConnectivityPage'),
      'ConnectivityPage',
    ),
  }),
] as const;
